import { EscalationLayer } from '@eppendorf/vnls-notifications-utils';
import { Icon, IconSizeClasses, Tooltip } from '@eppendorf/vnls-react-components';
import { ReactElement, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { InfoList } from '$features/devices/device-information/info-list';
import { NotificationsDetailEscalationLayer } from '$features/notifications/detail/notification-detail-escalation-layer';
import { renderEscalateAfter } from '$features/notifications/detail/utils';
import './notification-detail-recipient.scss';

type NotificationDetailRecipientProps = {
  escalationLayers: EscalationLayer[];
  tags: string[];
  notificationCadence: number;
  notificationSendDelaySeconds?: number;
};

export function NotificationDetailRecipient({
  escalationLayers,
  tags,
  notificationCadence,
  notificationSendDelaySeconds,
}: NotificationDetailRecipientProps): ReactElement {
  const escalationLayersCount = escalationLayers.length;
  const { t } = useTranslation();
  const renderTitle = useMemo(() => {
    if (escalationLayersCount === 1)
      return t(`notificationDetail.oneGroupEscalationLayerDescription`, {
        timeOne: renderEscalateAfter(escalationLayers[0].escalateAfter),
      });
    if (escalationLayersCount === 2)
      return t(`notificationDetail.twoGroupsEscalationLayerDescription`, {
        timeOne: renderEscalateAfter(escalationLayers[0].escalateAfter),
        timeTwo: renderEscalateAfter(escalationLayers[1].escalateAfter),
      });
    if (escalationLayersCount === 3)
      return t(`notificationDetail.threeGroupsEscalationLayerDescription`, {
        timeOne: renderEscalateAfter(escalationLayers[0].escalateAfter),
        timeTwo: renderEscalateAfter(escalationLayers[1].escalateAfter),
        timeThree: renderEscalateAfter(escalationLayers[2].escalateAfter),
      });

    return null;
  }, [escalationLayersCount, escalationLayers]);

  const renderEscalationLayers = useMemo(() => {
    if (escalationLayersCount === 1 && tags.length === 1 && tags.includes('info')) {
      return escalationLayers[0].recipients.map((recipient, index) => (
        <div
          key={recipient.name}
          className={
            index === escalationLayers[0].recipients.length - 1 ? '' : 'p-bottom-s'
          }
        >
          {recipient.name}
        </div>
      ));
    }

    return (
      <>
        <div className="m-bottom-xxl">{renderTitle}</div>
        <Tooltip
          trigger={
            <div
              className="notification-detail-recipient__notifications-badge border-radius-m font-weight-semibold p-top-s p-bottom-s p-m bg-blue-800 color-white"
              style={{ marginBottom: 0 }}
            >
              <div className="p-right-s">
                <Icon size={IconSizeClasses.XSmall} name="delay" className="bg-white" />
              </div>
              {!notificationSendDelaySeconds
                ? t('notificationDetail.noDelay')
                : t('notificationDetail.notificationDelayTitle', {
                    value: `${notificationSendDelaySeconds / 60} ${t('shared.min')}`,
                  })}
            </div>
          }
        >
          <Trans
            i18nKey={
              !notificationSendDelaySeconds
                ? 'notificationDetail.tooltipViewNoDelay'
                : 'notificationDetail.tooltipView'
            }
            components={{
              bold: <span className="font-weight-bold" />,
              regular: <span style={{ opacity: 0.8 }} />,
            }}
          />
        </Tooltip>
        <div className="notification-delay__tail--after2" />
        <div className="notification-detail-recipient__notifications-badge p-m border-s border-solid border-color-gray-500 m-bottom-xxl">
          <div className="notification-detail-recipient__notifications-badge--icon">
            <Icon
              size={IconSizeClasses.XXSmall}
              name="alarm-ringing"
              className="bg-white"
            />
          </div>
          {t('notificationDetail.notificationFrequencyTitle', {
            value: `${notificationCadence} ${t('shared.min')}`,
          })}
        </div>
        {escalationLayers.map((escalationLayer, index) => (
          <NotificationsDetailEscalationLayer
            key={`escalation-layer-${index.toString()}`}
            escalationLayer={escalationLayer}
            index={index}
            totalLayers={escalationLayers.length}
          />
        ))}
      </>
    );
  }, [
    escalationLayersCount,
    escalationLayers,
    t,
    tags,
    notificationCadence,
    notificationSendDelaySeconds,
  ]);

  return (
    <InfoList>
      <InfoList.Title>{t('notificationDetail.recipients')}</InfoList.Title>
      <div className="m-top-l">{renderEscalationLayers}</div>
    </InfoList>
  );
}
